import { memo, useMemo } from 'react';
import { HStack } from '../../Core';
import { useModalContext } from '../../Modal/Modal';
import { Tab, TabList, TabPanel, Tabs } from '../../Tabs';
import { AutocompleteResults } from '../Autocomplete/AutocompleteResults';
import { Dropdown } from '../Dropdown';
import { AutocompleteResultsWrapper } from './styles';
import type { AutocompleteDropdownProps } from './types';

export const AutocompleteDropdown = <T,>({
  menuStyles,
  isOpen,
  size,
  portalize,
  referenceElement,
  referenceWidth,
  dropdownWidth,
  dropdownHeight,
  setPopperElement,
  id,
  popper,
  onTabOut,
  onKeyDown,
  dropdownContentRef,
  childrenAboveResults,
  showGroupTabs,
  suppressGroupTabsOverflow,
  groupSorter,
  tabs,
  groupsPrefix,
  suffix,
  suffixCb,
  closeMenu,
  testID,
  ...autocompleteResultsProps
}: AutocompleteDropdownProps<T>) => {
  const modalContext = useModalContext();
  const portalizeToUse = portalize == null ? modalContext?.portalize : portalize;

  const selectedGroup = useMemo(() => {
    if (!showGroupTabs || !tabs) {
      return undefined;
    }
    return tabs.items[tabs.selectedIndex]?.group;
  }, [showGroupTabs, tabs]);

  const renderedSuffix = useMemo(() => {
    if (suffixCb) {
      return suffixCb({ selectedGroup, closeMenu });
    }
    return suffix ?? null;
  }, [suffixCb, suffix, selectedGroup, closeMenu]);

  const AutoCompleteResults = useMemo(
    () => (
      <>
        {childrenAboveResults}
        <AutocompleteResults isOpen={isOpen} closeMenu={closeMenu} {...autocompleteResultsProps} />
      </>
    ),
    [autocompleteResultsProps, childrenAboveResults, isOpen, closeMenu]
  );

  return (
    <Dropdown
      menuStyles={menuStyles}
      isOpen={isOpen}
      size={size}
      portalize={portalizeToUse}
      referenceElement={referenceElement}
      referenceWidth={referenceWidth}
      dropdownWidth={dropdownWidth}
      setPopperElement={setPopperElement}
      popper={popper}
      dropdownContentRef={dropdownContentRef}
      onTabOut={onTabOut}
      onKeyDown={onKeyDown}
      id={id}
    >
      <AutocompleteResultsWrapper
        data-testid={testID ?? 'autocomplete-results-wrapper'}
        {...autocompleteResultsProps.getMenuProps(
          {},
          {
            // If portalize is used, the ref error is suppressed since
            // the ref is not available in the same context
            suppressRefError: portalizeToUse,
          }
        )}
      >
        {showGroupTabs ? (
          <Tabs {...tabs}>
            <GroupTabs
              tabs={tabs}
              groupsPrefix={groupsPrefix}
              showGroupTabs={showGroupTabs}
              suppressGroupTabsOverflow={suppressGroupTabsOverflow}
            />
            <TabPanel>{AutoCompleteResults}</TabPanel>
          </Tabs>
        ) : (
          AutoCompleteResults
        )}
        {renderedSuffix}
      </AutocompleteResultsWrapper>
    </Dropdown>
  );
};

const GroupTabs = memo(function GroupTabs<T>({
  tabs,
  groupsPrefix,
  showGroupTabs,
  suppressGroupTabsOverflow,
}: {
  tabs: AutocompleteDropdownProps<T>['tabs'];
  groupsPrefix: AutocompleteDropdownProps<T>['groupsPrefix'];
  showGroupTabs: AutocompleteDropdownProps<T>['showGroupTabs'];
  suppressGroupTabsOverflow: AutocompleteDropdownProps<T>['suppressGroupTabsOverflow'];
}) {
  return (
    <HStack>
      {groupsPrefix}
      <TabList isBordered aria-hidden={!showGroupTabs} suppressOverflowList={suppressGroupTabsOverflow}>
        {tabs.items.map((tab, idx) => (
          // The overflow ("X More") tabs render in the popover portal, outside Downshift's menu node, so a
          // plain mousedown reads as an outside click and closes the dropdown. Keeping focus on the input
          // makes Downshift treat the click as inside.
          <Tab {...tab} label={tab.group} key={tab.group} onMouseDown={event => event.preventDefault()} />
        ))}
      </TabList>
    </HStack>
  );
});
